/* @flow */
import { get, merge, flatten, flow, flatMap, uniqBy, minBy } from 'lodash';
import { prettifyTime } from './string';
import moment from 'moment';

import type {
  Person,
  Track,
  SimplifiedTrack,
  SimplifiedMedia,
  Media,
  CoverSize,
  Quality,
  Worklist,
  Usage,
  CreateUsageParams,
  UpdateUsageParams,
  SaveUsageParams,
  SaveWorklistParams,
  WorklistItem,
  MergedLicenseComponent,
} from 'types';
import type { Location, RouterHistory } from 'react-router-dom';

// ===========================
//   TRACK UTILITIES
// ===========================
const getMainMedia = (track: Track): SimplifiedMedia | void => {
  return getOldestMedia(track.medias);
};

const getCoverUrlBySizeFromMedias = (track: Track, size: CoverSize): string | void => {
  const media = getMainMedia(track);
  const covers = get(media, 'covers', []);
  const sizedCover = covers.find(cover => cover.size === size);
  return sizedCover ? sizedCover.url : undefined;
};

const getTrackCoverUrl = (track: Track): string | void => {
  return getCoverUrlBySizeFromMedias(track, 'MEDIUM');
};

const getTrackUrlByQualityFromTrack = (
  track: ?SimplifiedTrack,
  quality: Quality
): string | void => {
  return get(track, ['resources', quality, 'url']);
};

const getTrackAudioUrl = (track: SimplifiedTrack): string => {
  const trackUrlHd = getTrackUrlByQualityFromTrack(track, 'hd');
  if (trackUrlHd) return trackUrlHd;
  const trackUrlSd = getTrackUrlByQualityFromTrack(track, 'sd');
  if (trackUrlSd) return trackUrlSd;
  const trackUrl_ = getTrackUrlByQualityFromTrack(track, '_');
  if (trackUrl_) return trackUrl_;
  return '';
};

// ===========================
//   PERSON UTILITIES
// ===========================
const getPersonLabel = (person: Person): string => {
  return person.alias ? `${person.alias} (${person.publicName})` : person.publicName;
};

const getPersonShortLabel = (person: Person): string => {
  return person.alias || person.publicName;
};

// ===========================
//   WORKLIST UTILITIES
// ===========================
const getWorklistCoverUrl = (worklist?: Worklist) => {
  if (!worklist || !worklist.coverUrl) {
    return undefined;
  }
  return worklist.coverUrl;
};

const getWorklistSaveParamsFromWorklist = (worklist: ?Worklist): SaveWorklistParams => {
  const worklistParams: SaveWorklistParams = {
    id: get(worklist, 'id', null),
    name: get(worklist, 'name', 'Worklist'),
    description: get(worklist, 'description', ''),
    parentWorklistId: get(worklist, 'parentWorklistId', null),
    usage: {
      id: get(worklist, 'usage.id', null),
      name: get(worklist, 'usage.name', ''),
      startDate: get(worklist, 'usage.startDate', null),
      startDateLocked: get(worklist, 'usage.startDateLocked', false),
      endDate: get(worklist, 'usage.endDate', null),
      endDateLocked: get(worklist, 'usage.endDateLocked', false),
      exclusive: get(worklist, 'usage.exclusive', false),
      exclusiveLocked: get(worklist, 'usage.exclusiveLocked', false),
      exclusiveEndDate: get(worklist, 'usage.exclusiveEndDate', null),
      exclusiveEndDateLocked: get(worklist, 'usage.exclusiveEndDateLocked', false),
      exclusiveNotes: get(worklist, 'usage.exclusiveNotes', ''),
      notes: get(worklist, 'usage.notes', ''),
      notesLocked: get(worklist, 'usage.notesLocked', false),
      productionId: get(worklist, 'usage.production.id', null),
      episode: get(worklist, 'usage.episode', ''),
      episodeLocked: get(worklist, 'usage.episodeLocked', false),
      channelId: get(worklist, 'usage.channelId', null),
      channelLocked: get(worklist, 'usage.channelLocked', false),
      broadcastingId: get(worklist, 'usage.broadcastingId', null),
      broadcastingLocked: get(worklist, 'usage.broadcastingLocked', false),
      platformId: get(worklist, 'usage.platformId', null),
      platformLocked: get(worklist, 'usage.platformLocked', false),
      broadcastTypeId: get(worklist, 'usage.broadcastTypeId', null),
      broadcastTypeLocked: get(worklist, 'usage.broadcastTypeLocked', false),
      territoryId: get(worklist, 'usage.territory.id', null),
      territoryLocked: get(worklist, 'usage.territoryLocked', false),
      usageTypeId: get(worklist, 'usage.usageType.id', null),
      usageTypeLocked: get(worklist, 'usage.usageTypeLocked', false),
    },
  };
  return worklistParams;
};

// ===========================
//   USAGE UTILITIES
// ===========================
const parseDateMillis = (value: string): number => moment(value, 'DD/MM/YYYY').unix() * 1000;
const dateMillisToMoment = (value: number): any => (value ? moment.unix(value / 1000) : null);

const getUsageFormValuesFromUsage = (usage?: Usage) => {
  return {
    ...getUsageUpdateParamsFromObject(usage),
    productionId: get(usage, 'production.id', null),
    channelId: get(usage, 'channel.id', null),
    broadcastingId: get(usage, 'broadcasting.id', null),
    platformId: get(usage, 'platform.id', null),
    broadcastTypeId: get(usage, 'broadcastType.id', null),
    territoryId: get(usage, 'territory.id', null),
    usageTypeId: get(usage, 'usageType.id', null),
  };
};

const getUsageCreateParamsFromObject = (obj?: any): CreateUsageParams => {
  return {
    name: get(obj, 'name', ''),
    startDate: get(obj, 'startDate', null),
    startDateLocked: get(obj, 'startDateLocked', false),
    endDate: get(obj, 'endDate', null),
    endDateLocked: get(obj, 'endDateLocked', false),
    exclusive: get(obj, 'exclusive', false),
    exclusiveLocked: get(obj, 'exclusiveLocked', false),
    exclusiveEndDate: get(obj, 'exclusiveEndDate', null),
    exclusiveEndDateLocked: get(obj, 'exclusiveEndDateLocked', false),
    exclusiveNotes: get(obj, 'exclusiveNotes', ''),
    exclusiveNotesLocked: get(obj, 'exclusiveNotesLocked', false),
    notes: get(obj, 'notes', ''),
    notesLocked: get(obj, 'notesLocked', false),
    productionId: get(obj, 'productionId', null),
    productionLocked: get(obj, 'productionLocked', false),
    channelId: get(obj, 'channelId', null),
    channelLocked: get(obj, 'channelLocked', false),
    broadcastingId: get(obj, 'broadcastingId', null),
    broadcastingLocked: get(obj, 'broadcastingLocked', false),
    platformId: get(obj, 'platformId', null),
    platformLocked: get(obj, 'platformLocked', false),
    broadcastTypeId: get(obj, 'broadcastTypeId', null),
    broadcastTypeLocked: get(obj, 'broadcastTypeLocked', false),
    episode: get(obj, 'episode', ''),
    episodeLocked: get(obj, 'episodeLocked', false),
    quantity: get(obj, 'quantity', null),
    quantityLocked: get(obj, 'quantityLocked', false),
    status: get(obj, 'status', false),
    statusLocked: get(obj, 'statusLocked', false),
    territoryId: get(obj, 'territoryId', null),
    territoryLocked: get(obj, 'territoryLocked', false),
    usageTypeId: get(obj, 'usageTypeId', null),
    usageTypeLocked: get(obj, 'usageTypeLocked', false),
  };
};

const getUsageUpdateParamsFromObject = (obj?: any): UpdateUsageParams => {
  return {
    id: get(obj, 'id', null),
    ...getUsageCreateParamsFromObject(obj),
  };
};

const getUsageSaveParamsFromObject = (obj?: any): SaveUsageParams => {
  return getUsageUpdateParamsFromObject(obj);
};

const getCanBuyLicenseComponent = (mergedComponent: MergedLicenseComponent) => {
  return (
    mergedComponent.components != null && mergedComponent.components.some(c => c.price != null)
  );
};

const getCanAskQuotationForLicenseComponent = (mergedComponent: MergedLicenseComponent) => {
  return (
    mergedComponent.components == null ||
    (mergedComponent.components.every(c => c.price == null) &&
      mergedComponent.components.every(c => c.quotation == null))
  );
};

const getCanBuyWorklistItem = (item: WorklistItem) => {
  return item.mergedComponents != null && item.mergedComponents.some(getCanBuyLicenseComponent);
};

const getCanAskQuotationForWorklistItem = (item: WorklistItem) => {
  return (
    item.mergedComponents == null ||
    item.mergedComponents.length === 0 ||
    item.mergedComponents.some(getCanAskQuotationForLicenseComponent)
  );
};

const getLicenseComponentsTotalPrice = (mergedComponents: MergedLicenseComponent[]) => {
  const allComponents = flatten(mergedComponents.map(({ components }) => components || []));
  const totalPrice =
    allComponents.reduce((acc, component) => acc + (component.price || 0) * 100, 0) / 100;
  return totalPrice;
};

const getLicenseComponentsTotalDiscountedPrice = (mergedComponents: MergedLicenseComponent[]) => {
  const allComponents = flatten(mergedComponents.map(({ components }) => components || []));
  const totalDiscountedPrice =
    allComponents.reduce((acc, component) => acc + (component.discountedPrice || 0) * 100, 0) / 100;
  return totalDiscountedPrice;
};

// A filter function to keep only 'Workcopy' components, i.e. "Prima Copia" & "Copia Tecnica"
const filterOutOptionalComponents = (worklistItem: WorklistItem): WorklistItem => ({
  ...worklistItem,
  mergedComponents:
    worklistItem.mergedComponents &&
    worklistItem.mergedComponents.filter(component => component.description === 'Workcopy'),
});

// ===========================
//   MEDIA UTILITIES
// ===========================
const getOldestMedia = (medias: SimplifiedMedia[]): SimplifiedMedia | void => {
  return minBy(medias, media => media.year || Number.MAX_SAFE_INTEGER);
};
const getCoverUrlBySizeFromMedia = (media: SimplifiedMedia, size: CoverSize): ?string => {
  const sizedCover = media.covers.find(cover => cover.size === size);
  return sizedCover ? sizedCover.url : null;
};

const getMediaCoverUrl = (media: SimplifiedMedia): ?string => {
  return getCoverUrlBySizeFromMedia(media, 'EXTRALARGE');
};

const getMediaLabel = (media: SimplifiedMedia): string =>
  [media.title, media.year, media.label && media.label.name].filter(_ => _).join(' - ');

const getMediaDetails = (media: SimplifiedMedia) =>
  `${media.availableTracksCount} Tracce - Durata totale ${prettifyTime(
    media.availableTracksDuration
  )}`;

const getMediaPerformersLabels = (media: Media): string[] => {
  const labels = media.performers.map(getPersonLabel);
  if (labels.length !== 0) {
    return labels;
  }
  const uniqTracksPerformers: Person[] = flow([
    _ => flatMap(_, _ => get(_, 'performers')),
    _ => uniqBy(_, _ => get(_, 'id')),
  ])(media.tracks);
  if (uniqTracksPerformers.length === 0) {
    return ['Nessun artista'];
  } else if (uniqTracksPerformers.length === 1) {
    return uniqTracksPerformers.map(getPersonLabel);
  } else {
    return ['Artisti vari'];
  }
};

const mergeUsages = (trackUsage: ?Usage, worklistUsage: ?Usage, sessionUsage: ?Usage): Usage =>
  merge({}, trackUsage, worklistUsage, sessionUsage);

const goTo = (
  { location, history }: { location: Location, history: RouterHistory },
  destination: string
) => {
  location.pathname !== destination && history.push(destination);
};

const openInNewTab = (
  { location, history }: { location: Location, history: RouterHistory },
  destination: string
) => {
  var win = window.open(destination, '_blank');
  win.focus();
};

const fromRoutes = {
  worklistId: (pathname: string): string | null => {
    const tenantRegex = /\/worklists\/([^/?#]+)/i;
    const found = pathname.match(tenantRegex);
    return (found && found[1]) || null;
  },
};

const routes = {
  tracks: {
    find: () => '/tracks/search',
  },
  medias: {
    find: () => '/medias/search',
    get: (id: number) => `/medias/${id}`,
  },
  news: {
    find: () => '/news',
  },
  usages: {
    find: () => '/usages',
    get: (id: number) => `/usages/${id}`,
    edit: (id: number) => `/usages/${id}/edit`,
    create: () => '/usages/create',
  },
  worklists: {
    find: () => '/worklists',
    get: (id: number) => `/worklists/${id}`,
    edit: (id: number) => `/worklists/${id}/edit`,
    create: () => '/worklists/create',
  },
  productions: {
    find: () => '/productions',
    get: (id: number) => `/productions/${id}`,
    edit: (id: number) => `/productions/${id}/edit`,
    create: () => '/productions/create',
  },
  cart: {
    items: {
      find: () => '/cart',
    },
  },
  smartlists: {
    find: () => '/smartlists',
    get: (id: number) => `/smartlists/${id}`,
  },
  featured: {
    find: () => '/featured',
    get: (id: number) => `/featured/${id}`,
  },
  ondemand: {
    find: () => `/ondemand`,
  },
  purchases: {
    find: () => '/purchases',
  },
  profile: {
    find: () => '/profile',
  },
  pricelists: {
    find: () => `https://protones.it/listini`,
  },
  tutorial: {
    find: () => `https://protones.it/tutorial`,
  },
  legalNotes: {
    find: () => `https://protones.it/note-legali`,
  },
  help: {
    find: () => `https://protones.it/assistenza`,
  },
};
export default {
  getMainMedia,
  getOldestMedia,
  getTrackCoverUrl,
  getTrackAudioUrl,
  getPersonLabel,
  getPersonShortLabel,
  getWorklistCoverUrl,
  getWorklistSaveParamsFromWorklist,
  getUsageFormValuesFromUsage,
  getUsageCreateParamsFromObject,
  getUsageUpdateParamsFromObject,
  getUsageSaveParamsFromObject,
  getCanBuyLicenseComponent,
  getCanBuyWorklistItem,
  getCanAskQuotationForLicenseComponent,
  getCanAskQuotationForWorklistItem,
  getLicenseComponentsTotalPrice,
  getLicenseComponentsTotalDiscountedPrice,
  filterOutOptionalComponents,
  getMediaCoverUrl,
  getMediaLabel,
  getMediaDetails,
  getMediaPerformersLabels,
  mergeUsages,
  parseDateMillis,
  dateMillisToMoment,
  openInNewTab,
  goTo,
  fromRoutes,
  routes,
};
